<template>
  <div class="markup-session" :class="{ 'markup-session-embedded': hideToolbar }">
    <!-- Main Content -->
    <div class="markup-content" :class="{ 'markup-content-embedded': hideToolbar }">
      <v-container fluid class="pa-0 ma-0 markup-session-container">
        <v-row no-gutters class="fill-height">
          <!-- Preview Area -->
          <v-col cols="12" md="8" class="preview-column">
            <!-- Loading Overlay for Initial Data -->
            <v-overlay :value="loading" absolute z-index="100">
              <v-progress-circular
                indeterminate
                size="64"
                color="primary"
              ></v-progress-circular>
            </v-overlay>

            <!-- Header -->
            <v-toolbar v-if="!hideToolbar" flat color="white" class="custom-toolbar" height="64">
              <v-btn icon @click="goBack">
                <v-icon>mdi-close</v-icon>
              </v-btn>

              <v-toolbar-title class="ml-2 text-h6 font-weight-regular">
                {{ sessionName }}
              </v-toolbar-title>

              <v-chip
                v-if="!isLatestVersion"
                class="ml-4"
                small
                color="warning"
                outlined
              >
                <v-icon small left>mdi-information-outline</v-icon>
                {{ $t('markup.old_version_readonly') }}
              </v-chip>

              <v-spacer></v-spacer>
            </v-toolbar>

            <div class="preview-container" @click="handlePreviewClick">
              <!-- Loading State -->
              <v-skeleton-loader
                v-if="loading"
                type="image"
                height="100%"
              ></v-skeleton-loader>

              <!-- Preview Content -->
              <div
                v-else
                class="preview-wrapper"
                ref="previewWrapper"
                @mousedown="handleDrawStart"
                @mousemove="handleDrawMove"
                @mouseup="handleDrawEnd"
              >
                <!-- Image/PDF Page Preview -->
                <img
                  v-if="currentVersion && (!hasPreviewRendition || isImagePreviewMimeType || isPaginatedDocument || !isVideoPreviewMimeType)"
                  :src="currentPreviewUrl"
                  class="preview-image"
                  :class="{ 'can-pan': zoomLevel > 100 && !addMarkupMode }"
                  :style="getImageTransform()"
                  ref="previewImage"
                  @load="onPreviewLoad"
                  @mousedown="handlePanStart"
                  @mousemove="handlePanMove"
                  @mouseup="handlePanEnd"
                  @mouseleave="handlePanEnd"
                  :draggable="false"
                  @dragstart.prevent
                />

                <!-- Video Preview -->
                <JobApprovalVideoPlayer
                  v-else-if="currentVersion && hasPreviewRendition && isVideoPreviewMimeType && useJobApprovalVideoPlayer"
                  :src="currentPreviewUrl"
                  ref="previewVideo"
                  class="preview-video-player"
                  :video-class="{ 'can-pan': zoomLevel > 100, 'markup-mode': addMarkupMode }"
                  :video-style="getImageTransform()"
                  @loadedmetadata="onPreviewLoad"
                  @timeupdate="onVideoTimeUpdate"
                  @video-mousedown="handlePanStart"
                  @video-mousemove="handlePanMove"
                  @video-mouseup="handlePanEnd"
                  @video-mouseleave="handlePanEnd"
                  @video-click="handleVideoClick"
                />

                <video
                  v-else-if="currentVersion && hasPreviewRendition && isVideoPreviewMimeType && !useJobApprovalVideoPlayer"
                  :src="currentPreviewUrl"
                  ref="previewVideo"
                  class="preview-video"
                  :class="{ 'can-pan': zoomLevel > 100, 'markup-mode': addMarkupMode }"
                  :style="getImageTransform()"
                  controls
                  @loadedmetadata="onPreviewLoad"
                  @timeupdate="onVideoTimeUpdate"
                  @mousedown="handlePanStart"
                  @mousemove="handlePanMove"
                  @mouseup="handlePanEnd"
                  @mouseleave="handlePanEnd"
                  @click="handleVideoClick"
                ></video>

                <!-- Magnifier Glass -->
                <div
                  v-if="magnifierMode && magnifierPos"
                  class="magnifier-glass"
                  :style="getMagnifierStyle()"
                ></div>

                <!-- Drawing Preview Overlay -->
                <div
                  v-if="isDrawing && drawStartPos && drawCurrentPos"
                  class="markup-transform-layer"
                  :style="getPreviewLayerStyle()"
                >
                  <div
                    class="drawing-preview"
                    :style="getDrawingPreviewStyle()"
                    :class="{ 'drawing-preview-circle': newMarkupShape === 'circle' }"
                  ></div>
                </div>

                <!-- Markup Indicators -->
                <div
                  class="markup-indicators"
                  v-show="markupsVisible"
                  :style="getPreviewLayerStyle()"
                >
                  <div
                    v-for="markup in currentPageMarkups"
                    :key="markup.markup_id"
                    class="markup-indicator"
                    :class="{
                      'markup-indicator-selected': selectedMarkup && selectedMarkup.markup_id === markup.markup_id,
                      'markup-indicator-resolved': markup.status === 'approved',
                      'markup-indicator-shape': markup.shape_type !== 'point'
                    }"
                    :style="getMarkupStyle(markup)"
                    @click.stop="selectMarkup(markup)"
                  >
                    <!-- Point markup: Show avatar with number -->
                    <v-avatar
                      v-if="markup.shape_type === 'point'"
                      :color="getMarkupColor(markup)"
                      size="32"
                      class="markup-avatar"
                    >
                      <span class="white--text font-weight-bold">{{ getMarkupGlobalIndex(markup) }}</span>
                    </v-avatar>

                    <!-- Rectangle/Circle markup: Show number in center -->
                    <div v-else class="shape-markup-number">
                      <v-avatar
                        :color="getMarkupColor(markup)"
                        size="28"
                        class="markup-avatar"
                      >
                        <span class="white--text font-weight-bold">{{ getMarkupGlobalIndex(markup) }}</span>
                      </v-avatar>
                    </div>
                  </div>
                </div>

                <!-- Add Markup Cursor -->
                <div
                  v-if="addMarkupMode"
                  class="add-markup-cursor"
                ></div>
              </div>

              <!-- Pagination Controls (for PDF/Documents) -->
              <div v-if="!loading && isPaginatedDocument" class="page-navigation">
                <v-btn
                  icon
                  large
                  class="page-nav-btn page-nav-left"
                  :disabled="currentPage <= 1"
                  @click.stop="previousPage"
                >
                  <v-icon large>mdi-chevron-left</v-icon>
                </v-btn>

                <v-chip class="page-indicator" color="primary" dark>
                  {{ currentPage }} / {{ totalPages }}
                </v-chip>

                <v-btn
                  icon
                  large
                  class="page-nav-btn page-nav-right"
                  :disabled="currentPage >= totalPages"
                  @click.stop="nextPage"
                >
                  <v-icon large>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Bottom Navigation -->
            <div class="bottom-navigation">
              <version-navigator
                v-if="versions.length > 0"
                :versions="versions"
                :current-version-id="currentVersionId"
                :zoom-level="zoomLevel"
                :magnifier-active="magnifierMode"
                :markups-visible="markupsVisible"
                :can-create-markup="isLatestVersion && canCreateMarkupAllowed"
                :can-classify="!!markupSetupEnabler"
                @version-change="changeVersion"
                @create-markup="openCreateMarkupDialog"
                @classification="openClassificationDialog"
                @zoom-in="zoomIn"
                @zoom-out="zoomOut"
                @toggle-magnifier="toggleMagnifier"
                @toggle-markups="toggleMarkups"
              />
            </div>
          </v-col>

          <!-- Sidebar -->
          <v-col cols="12" md="4" class="sidebar-column">
            <!-- Loading Overlay for Sidebar Actions -->
            <v-overlay :value="updatingMarkup || updatingVersion" absolute z-index="10">
              <v-progress-circular
                indeterminate
                size="64"
                color="primary"
              ></v-progress-circular>
            </v-overlay>

            <markup-list
              :markups="currentMarkups"
              :users="users"
              :selected-markup-id="selectedMarkup ? selectedMarkup.markup_id : null"
              :approval-status="currentVersion ? currentVersion.status : 'pending'"
              :can-approve="canEditMarkup && !hideApproval"
              :can-edit="canEditMarkup"
              @select-markup="selectMarkup"
              @status-change="changeVersionStatus"
              @markup-status-change="changeMarkupStatus"
              @delete-markup="deleteMarkup"
              @open-chat="openMarkupChat"
            />
          </v-col>
        </v-row>
      </v-container>
    </div>

    <version-classification-dialog ref="classificationDialog" />

    <!-- Create Markup Dialog -->
    <v-dialog v-model="showCreateMarkupDialog" max-width="500">
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ $t('markup.create_markup') }}</span>
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="newMarkupShape"
            :items="shapeTypes"
            :label="$t('markup.shape')"
            outlined
            class="mb-3"
          ></v-select>

          <v-select
            v-model="newMarkupType"
            :items="markupTypes"
            :label="$t('markup.type')"
            outlined
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showCreateMarkupDialog = false">
            {{ $t('markup.cancel') }}
          </v-btn>
          <v-btn color="success" @click="startMarkupCreation">
            {{ $t('markup.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Markup Message Dialog -->
    <v-dialog v-model="showAddMarkupDialog" max-width="500">
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ $t('markup.add_markup') }}</span>
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="newMarkupMessage"
            :label="$t('markup.message')"
            rows="3"
            outlined
            autofocus
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelAddMarkup" :disabled="creatingMarkup">
            {{ $t('markup.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmAddMarkup"
            :loading="creatingMarkup"
            :disabled="creatingMarkup"
          >
            {{ $t('markup.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ $t('markup.delete_markup') }}</span>
        </v-card-title>
        <v-card-text>
          {{ $t('markup.delete_confirmation') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDeleteMarkup" :disabled="deletingMarkup">
            {{ $t('markup.cancel') }}
          </v-btn>
          <v-btn
            color="error"
            @click="confirmDeleteMarkup"
            :loading="deletingMarkup"
            :disabled="deletingMarkup"
          >
            {{ $t('markup.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/services/api'
import { mapActions } from 'vuex'
import JobApprovalVideoPlayer from '@/components/job/JobApprovalVideoPlayer.vue'
import MarkupList from '@/components/content/markup/MarkupList.vue'
import VersionNavigator from '@/components/content/markup/VersionNavigator.vue'
import VersionClassificationDialog from '@/components/content/markup/VersionClassificationDialog.vue'
import UserActions from '@/store/core/user/actions-types'
import defaultPreviewImage from '@/assets/preview-placeholder.svg'
import { isScopedTokenRoute } from '@/services/scoped-token'

export default {
  name: 'MarkupSession',

  props: {
    // Session ID can be passed as prop (for embedding) or from route
    sessionId: {
      type: [Number, String],
      default: null
    },
    // Hide the top toolbar (useful when embedded in dialogs)
    hideToolbar: {
      type: Boolean,
      default: false
    },
    // Whether user can edit markup (based on ACL)
    canEditMarkup: {
      type: Boolean,
      default: true
    },
    // An explicit creation permission can be supplied by external invitations.
    canCreateMarkup: {
      type: Boolean,
      default: null
    },
    hideApproval: {
      type: Boolean,
      default: false
    },
    useJobApprovalVideoPlayer: {
      type: Boolean,
      default: false
    }
  },

  components: {
    JobApprovalVideoPlayer,
    MarkupList,
    VersionNavigator,
    VersionClassificationDialog
  },

  data() {
    return {
      loading: true,
      session: null,
      versions: [],
      currentVersionId: null,
      markupSetupEnabler: null,
      markups: [],
      selectedMarkup: null,
      users: [],
      addMarkupMode: false,
      showAddMarkupDialog: false,
      newMarkupMessage: '',
      newMarkupShape: 'point',
      newMarkupColor: '#2196F3',
      pendingMarkupCoords: null,
      currentVideoTime: 0,
      videoMarkupTimeWindow: 1, // Time window in seconds to show markups around current timestamp
      currentPage: 1, // For paginated documents
      previewDimensions: {
        width: 0,
        height: 0
      },
      previewLayoutBox: {
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        screenLeft: 0,
        screenTop: 0
      },
      previewResizeObserver: null,
      shapeTypes: [],
      markupTypes: [],
      showCreateMarkupDialog: false,
      newMarkupType: 'correction',
      // Delete confirmation dialog
      showDeleteDialog: false,
      markupToDelete: null,
      // Chat dialog
      showChatDialog: false,
      chatMarkup: null,
      chatMessages: [],
      loadingChat: false,
      newChatMessage: '',
      sendingChat: false,
      // API operation loading states
      creatingMarkup: false,
      updatingMarkup: false,
      deletingMarkup: false,
      updatingVersion: false,
      // Drawing state
      isDrawing: false,
      drawStartPos: null,
      drawCurrentPos: null,
      // Zoom state
      zoomLevel: 100,
      // Magnifier state
      magnifierMode: false,
      magnifierPos: null,
      // Pan state
      isPanning: false,
      panStart: null,
      panOffset: { x: 0, y: 0 },
      // Markup visibility
      markupsVisible: true
    }
  },

  computed: {
    canCreateMarkupAllowed() {
      return this.canCreateMarkup ?? this.canEditMarkup
    },
    classificationSecurityGroupId() {
      const rawParams = this.markupSetupEnabler?.params ?? this.markupSetupEnabler?.enabler_params
      try {
        const params = typeof rawParams === 'string' ? JSON.parse(rawParams) : rawParams
        return params?.versioning_classification?.security_group_id
      } catch (error) {
        return null
      }
    },

    effectiveSessionId() {
      // Use prop if provided, otherwise get from route
      return this.sessionId || parseInt(this.$route.params.sessionId)
    },

    sessionName() {
      return this.session ? this.session.name : 'Carregando...'
    },

    currentVersion() {
      return this.versions.find(v => v.markup_session_node_id === this.currentVersionId)
    },

    currentVersionMimeType() {
      return this.currentVersion?.file_info?.mimetype || ''
    },

    isImagePreviewMimeType() {
      return this.currentVersionMimeType.startsWith('image/') ||
        ['x-indesign', 'postscript', 'officedocument.spreadsheetml', 'gltf-binary', 'octet-stream', 'pdf'].some(type => this.currentVersionMimeType.includes(type))
    },

    isVideoPreviewMimeType() {
      return this.currentVersionMimeType.startsWith('video/') ||
        this.currentVersionMimeType.startsWith('audio/') ||
        ['mxf'].some(type => this.currentVersionMimeType.includes(type))
    },

    previewRenditions() {
      if (!this.currentVersion || !this.currentVersion.renditions) return []

      // Filter only preview renditions and sort by order
      return this.currentVersion.renditions
        .filter(r => r.type === 'preview')
        .sort((a, b) => parseInt(a.order) - parseInt(b.order))
    },

    hasPreviewRendition() {
      return this.previewRenditions.length > 0
    },

    currentPreviewUrl() {
      if (!this.currentVersion) return defaultPreviewImage

      const previews = this.previewRenditions
      if (previews.length === 0) return defaultPreviewImage

      // For paginated documents, get the preview for the current page (index starts at 0)
      if (this.isPaginatedDocument) {
        const pageIndex = this.currentPage - 1
        return previews[pageIndex]?.url || previews[0]?.url || defaultPreviewImage
      }

      // For non-paginated, just return the first preview
      return previews[0]?.url || defaultPreviewImage
    },

    currentMarkups() {
      // Return all markups for the current version (excluding cancelled)
      // Don't filter by page - show all markups in the sidebar
      return this.markups.filter(m =>
        m.markup_session_node_id === this.currentVersionId &&
        m.status !== 'cancelled'
      )
    },

    currentPageMarkups() {
      // Markups to display on the current preview/page only (excluding cancelled)
      let filtered = this.markups.filter(m =>
        m.markup_session_node_id === this.currentVersionId &&
        m.status !== 'cancelled'
      )

      // Filter by current page for paginated documents
      if (this.isPaginatedDocument) {
        filtered = filtered.filter(m =>
          m.page_number === null || m.page_number === this.currentPage
        )
      }

      // Filter by timestamp for video previews
      if (this.isVideoPreviewMimeType) {
        filtered = filtered.filter(m => {
          // Show markup if it doesn't have a timestamp (legacy markups)
          if (m.timestamp_video === null || m.timestamp_video === undefined) {
            return true
          }

          // Show markup if current video time is within the time window
          const timeDiff = Math.abs(this.currentVideoTime - m.timestamp_video)
          return timeDiff <= this.videoMarkupTimeWindow
        })
      }

      return filtered
    },

    isPaginatedDocument() {
      // A document is paginated if it has multiple preview renditions
      return this.previewRenditions.length > 1
    },

    totalPages() {
      // Total pages is the number of preview renditions
      return this.previewRenditions.length || 1
    },

    isLatestVersion() {
      if (this.versions.length === 0 || !this.currentVersionId) return true

      // The latest version is the last one in the array
      const latestVersion = this.versions[this.versions.length - 1]
      return latestVersion && latestVersion.markup_session_node_id === this.currentVersionId
    }
  },

  async mounted() {
    // Initialize translated options
    this.shapeTypes = [
      { text: this.$t('markup.point'), value: 'point' },
      { text: this.$t('markup.rectangle'), value: 'rectangle' },
      { text: this.$t('markup.circle'), value: 'circle' }
    ]
    this.markupTypes = [
      { text: this.$t('markup.correction'), value: 'correction' },
      { text: this.$t('markup.alteration'), value: 'alteration' }
    ]

    await Promise.all([this.loadSession(), this.loadMarkupSetup()])
    await this.loadUsers()
    await this.loadVersions()
    await this.loadMarkups()
    this.loading = false
    this.$nextTick(() => {
      this.updatePreviewLayoutBox()
      this.observePreviewResize()
    })
    window.addEventListener('resize', this.handlePreviewResize)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handlePreviewResize)

    if (this.previewResizeObserver) {
      this.previewResizeObserver.disconnect()
      this.previewResizeObserver = null
    }
  },

  watch: {
    zoomLevel(newVal) {
      // Reset pan offset when zoom is at 100% or less
      if (newVal <= 100) {
        this.panOffset = { x: 0, y: 0 }
      }
    },

    currentPreviewUrl() {
      this.$nextTick(() => {
        this.observePreviewResize()
        this.updatePreviewLayoutBox()
      })
    }
  },

  methods: {
    async loadMarkupSetup() {
      this.markupSetupEnabler = null

      // Public approval links authenticate with a scoped token that cannot
      // access the Node API. Classification is not available in this flow.
      if (isScopedTokenRoute(this.$route)) return

      try {
        const response = await api.node.enabler.getEnablers({ limit: 9999 })
        this.markupSetupEnabler = (response.data.items || [])
          .find(enabler => enabler.aspect_key === 'markup_setup') || null
      } catch (error) {
        console.error('Error loading markup setup enabler:', error)
      }
    },

    openClassificationDialog() {
      if (!this.markupSetupEnabler) return
      this.$refs.classificationDialog.open(
        this.classificationSecurityGroupId,
        this.currentVersion?.node_id,
        this.canEditMarkup
      )
    },

    ...mapActions('core/user', [
      UserActions.GET_ALL_USERS,
    ]),

    async loadUsers() {
      try {
        const usersOptions = {
          'filter[include_foreign]': true,
          'limit': 9999,
        }
        const result = await this[UserActions.GET_ALL_USERS](usersOptions)
        this.users = [...result.items]
      } catch (error) {
        console.error('Error loading users:', error)
      }
    },

    async loadSession() {
      // Skip loading session details for now - only contains the name
      // Just create a basic session object with the ID
      this.session = {
        markup_session_id: this.effectiveSessionId,
        name: `Sessão de Marcação #${this.effectiveSessionId}`
      }
    },

    async loadVersions() {
      try {
        const response = await api.content.markup.getMarkupSessionNodesBySession(
          this.effectiveSessionId
        )
        // Sort versions by version number
        this.versions = response.data.items.sort((a, b) => a.version - b.version)

        // Set current version to the latest (last one after sorting)
        if (this.versions.length > 0) {
          this.currentVersionId = this.versions[this.versions.length - 1].markup_session_node_id
        }
      } catch (error) {
        console.error('Error loading versions:', error)
      }
    },

    async loadMarkups() {
      try {
        const response = await api.content.markup.getMarkupsBySession(this.effectiveSessionId, { limit: 9999 })
        this.markups = response.data.items
        this.emitMarkupCount()
      } catch (error) {
        console.error('Error loading markups:', error)
      }
    },

    changeVersion(version) {
      this.currentVersionId = version.markup_session_node_id
      this.selectedMarkup = null
    },

    selectMarkup(markup) {
      this.selectedMarkup = markup

      // Navigate to page for paginated documents
      if (markup.page_number !== null && markup.page_number !== undefined && this.isPaginatedDocument) {
        this.currentPage = markup.page_number
      }

      // Navigate to timestamp for videos
      if (markup.timestamp_video !== null && markup.timestamp_video !== undefined) {
        this.setPreviewVideoCurrentTime(markup.timestamp_video)
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.selectedMarkup = null
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.selectedMarkup = null
      }
    },

    handlePreviewClick(event) {
      if (!this.addMarkupMode) return

      // Only handle point markups here. Rectangle/Circle need to be drawn.
      if (this.newMarkupShape !== 'point') return

      // Prevent default behavior and stop propagation for point markups
      event.preventDefault()
      event.stopPropagation()

      const { x, y } = this.getDocumentCoordsFromEvent(event)

      this.pendingMarkupCoords = { x, y }
      this.showAddMarkupDialog = true
    },

    async confirmAddMarkup() {
      if (!this.canCreateMarkupAllowed || !this.isLatestVersion) return
      if (!this.newMarkupMessage || !this.pendingMarkupCoords) return

      this.creatingMarkup = true
      try {
        // Determine color based on markup type
        const color = this.newMarkupType === 'correction' ? 'error' : 'info'

        const markupData = {
          markup_session_node_id: this.currentVersionId,
          message: this.newMarkupMessage,
          x_coordinate: this.pendingMarkupCoords.x,
          y_coordinate: this.pendingMarkupCoords.y,
          shape_type: this.newMarkupShape,
          markup_type: this.newMarkupType,
          color: color,
          status: 'pending'
        }

        // Add width and height for rectangle/circle shapes
        if (this.pendingMarkupCoords.width && this.pendingMarkupCoords.height) {
          markupData.width = this.pendingMarkupCoords.width
          markupData.height = this.pendingMarkupCoords.height
        }

        // Add page number for paginated documents
        if (this.isPaginatedDocument) {
          markupData.page_number = this.currentPage
        }

        // Add timestamp for video
        if (this.isVideoPreviewMimeType) {
          markupData.timestamp_video = Math.floor(this.currentVideoTime)
        }

        const response = await api.content.markup.createMarkup(markupData)
        this.markups.push(response.data)
        this.emitMarkupCount(response.data.markup_session_node_id)

        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.markup_added_successfully'),
          color: 'success'
        })

        this.cancelAddMarkup()
      } catch (error) {
        console.error('Error creating markup:', error)
        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.error_adding_markup'),
          color: 'error'
        })
      } finally {
        this.creatingMarkup = false
      }
    },

    cancelAddMarkup() {
      this.showAddMarkupDialog = false
      this.newMarkupMessage = ''
      this.newMarkupShape = 'point'
      this.newMarkupColor = '#2196F3'
      this.pendingMarkupCoords = null
      this.addMarkupMode = false
    },

    async changeMarkupStatus({ markup, status }) {
      if (!this.canEditMarkup) {
        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.no_permission_to_edit'),
          color: 'warning'
        })
        return
      }

      const previousStatus = markup.status
      const previousColor = markup.color

      // Determine the new color based on the new status
      const colorMap = {
        pending: markup.markup_type === 'correction' ? 'error' : 'warning',
        fixed: 'info',
        approved: 'success',
        rejected: 'error'
      }
      const newColor = colorMap[status] || markup.color

      this.updatingMarkup = true
      try {
        await api.content.markup.updateMarkup(markup.markup_id, {
          status,
          color: newColor
        })

        markup.status = status
        markup.color = newColor

        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.markup_status_updated_successfully'),
          color: 'success'
        })
      } catch (error) {
        console.error('Error updating markup:', error)

        markup.status = previousStatus
        markup.color = previousColor

        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.error_updating_markup'),
          color: 'error'
        })
      } finally {
        this.updatingMarkup = false
      }
    },

    deleteMarkup(markup) {
      if (!this.canEditMarkup) {
        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.no_permission_to_edit'),
          color: 'warning'
        })
        return
      }

      // Show confirmation dialog
      this.markupToDelete = markup
      this.showDeleteDialog = true
    },

    async confirmDeleteMarkup() {
      if (!this.markupToDelete) return

      this.deletingMarkup = true
      try {
        await api.content.markup.updateMarkup(this.markupToDelete.markup_id, {
          status: 'cancelled'
        })

        // Update the markup status in the local array using $set for reactivity
        const index = this.markups.findIndex(m => m.markup_id === this.markupToDelete.markup_id)
        if (index !== -1) {
          this.$set(this.markups[index], 'status', 'cancelled')
        }
        this.emitMarkupCount(this.markupToDelete.markup_session_node_id)

        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.markup_cancelled'),
          color: 'success'
        })

        this.showDeleteDialog = false
        this.markupToDelete = null
      } catch (error) {
        console.error('Error cancelling markup:', error)
        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.error_cancelling_markup'),
          color: 'error'
        })
      } finally {
        this.deletingMarkup = false
      }
    },

    cancelDeleteMarkup() {
      this.showDeleteDialog = false
      this.markupToDelete = null
    },

    emitMarkupCount(markupSessionNodeId = this.currentVersionId) {
      const version = this.versions.find(version =>
        version.markup_session_node_id === markupSessionNodeId
      )

      if (!version) return

      const count = this.markups.filter(markup =>
        markup.markup_session_node_id === markupSessionNodeId && markup.status !== 'cancelled'
      ).length

      this.$emit('markup-count-updated', {
        sessionId: this.effectiveSessionId,
        nodeId: version.node_id,
        count
      })
    },

    async changeVersionStatus(status) {
      if (!this.canEditMarkup) {
        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.no_permission_to_edit'),
          color: 'warning'
        })
        return
      }

      const previousStatus = this.currentVersion ? this.currentVersion.status : 'pending'

      this.updatingVersion = true
      try {
        await api.content.markup.updateMarkupSessionNode(this.currentVersionId, {
          status
        })

        const version = this.versions.find(v => v.markup_session_node_id === this.currentVersionId)
        if (version) {
          version.status = status

          // Emit status update event for parent components
          this.$emit('status-updated', {
            nodeId: version.node_id,
            status,
            version: version.version,
            sessionId: this.sessionId
          })
        }

        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.version_status_updated_successfully'),
          color: 'success'
        })
      } catch (error) {
        console.error('Error updating version status:', error)

        const version = this.versions.find(v => v.markup_session_node_id === this.currentVersionId)
        if (version) {
          version.status = previousStatus
        }

        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.error_updating_version_status'),
          color: 'error'
        })
      } finally {
        this.updatingVersion = false
      }
    },

    getMarkupStyle(markup) {
      const style = {
        left: `${markup.x_coordinate}%`,
        top: `${markup.y_coordinate}%`,
        position: 'absolute',
        transform: 'translate(-50%, -50%)'
      }

      if (markup.shape_type === 'rectangle' && markup.width && markup.height) {
        style.width = `${markup.width}%`
        style.height = `${markup.height}%`
        style.border = `3px solid ${this.getMarkupBorderColor(markup)}`
        style.borderRadius = '4px'
      } else if (markup.shape_type === 'circle' && markup.width && markup.height) {
        style.width = `${markup.width}%`
        style.height = `${markup.height}%`
        style.border = `3px solid ${this.getMarkupBorderColor(markup)}`
        style.borderRadius = '50%'
      }

      return style
    },

    getPreviewVideoElement() {
      const previewVideo = this.$refs.previewVideo

      if (!previewVideo) {
        return null
      }

      if (typeof previewVideo.getVideoElement === 'function') {
        return previewVideo.getVideoElement()
      }

      return previewVideo
    },

    getPreviewElement() {
      return this.$refs.previewImage || this.getPreviewVideoElement()
    },

    setPreviewVideoCurrentTime(currentTime) {
      const previewVideo = this.$refs.previewVideo

      if (previewVideo && typeof previewVideo.setCurrentTime === 'function') {
        previewVideo.setCurrentTime(currentTime)
        return
      }

      const video = this.getPreviewVideoElement()

      if (video) {
        video.currentTime = currentTime
      }
    },

    onPreviewLoad() {
      // Update preview dimensions for accurate markup positioning
      const preview = this.getPreviewElement()
      if (preview) {
        this.previewDimensions = {
          width: preview.clientWidth,
          height: preview.clientHeight
        }
      }
      this.$nextTick(() => {
        this.observePreviewResize()
        this.updatePreviewLayoutBox()
      })
    },

    onVideoTimeUpdate(event) {
      const video = event && event.target ? event.target : this.getPreviewVideoElement()

      this.currentVideoTime = video ? video.currentTime : 0
    },

    goBack() {
      this.$router.go(-1)
    },

    openCreateMarkupDialog() {
      if (!this.canCreateMarkupAllowed) {
        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.no_permission_to_edit'),
          color: 'warning'
        })
        return
      }

      // Only allow creating markup on the latest version
      if (!this.isLatestVersion) {
        this.$store.dispatch('app/showSnackbar', {
          text: this.$t('markup.cannot_create_markup_old_version'),
          color: 'warning'
        })
        return
      }

      this.showCreateMarkupDialog = true
    },

    startMarkupCreation() {
      if (!this.canCreateMarkupAllowed || !this.isLatestVersion) return
      // Close the create dialog
      this.showCreateMarkupDialog = false

      // Set the color based on markup type
      if (this.newMarkupType === 'correction') {
        this.newMarkupColor = '#F44336' // Red for corrections
      } else {
        this.newMarkupColor = '#2196F3' // Blue for alterations
      }

      // Enable markup mode
      this.addMarkupMode = true
    },

    getMarkupColor(markup) {
      // Use the color from the database directly
      return markup.color || 'primary'
    },

    getMarkupBorderColor(markup) {
      // Convert Vuetify color names to hex colors for borders
      const colorMap = {
        'success': '#4CAF50',
        'error': '#F44336',
        'info': '#2196F3',
        'primary': '#1976D2'
      }
      const colorName = this.getMarkupColor(markup)
      return colorMap[colorName] || colorMap.primary
    },

    getMarkupGlobalIndex(markup) {
      // Get the index of this markup in the full list (all pages)
      // This ensures consistent numbering across pages
      const index = this.currentMarkups.findIndex(m => m.markup_id === markup.markup_id)
      return index !== -1 ? index + 1 : 1
    },

    getDocumentCoordsFromEvent(event) {
      const previewBox = this.getPreviewLayoutBox()

      if (!previewBox.width || !previewBox.height) {
        return { x: 0, y: 0 }
      }

      const scale = this.zoomLevel / 100
      const pointX = event.clientX - previewBox.screenLeft
      const pointY = event.clientY - previewBox.screenTop
      const centerX = previewBox.width / 2
      const centerY = previewBox.height / 2

      const unscaledX = centerX + ((pointX - centerX - this.panOffset.x) / scale)
      const unscaledY = centerY + ((pointY - centerY - this.panOffset.y) / scale)

      return {
        x: this.clampPercent((unscaledX / previewBox.width) * 100),
        y: this.clampPercent((unscaledY / previewBox.height) * 100)
      }
    },

    getPreviewLayoutBox() {
      const preview = this.getPreviewElement()
      const wrapper = this.$refs.previewWrapper

      if (!preview || !wrapper) {
        return {
          left: 0,
          top: 0,
          width: 0,
          height: 0,
          screenLeft: 0,
          screenTop: 0
        }
      }

      const wrapperRect = wrapper.getBoundingClientRect()
      const left = preview.offsetLeft
      const top = preview.offsetTop
      const width = preview.clientWidth
      const height = preview.clientHeight

      return {
        left,
        top,
        width,
        height,
        screenLeft: wrapperRect.left + left,
        screenTop: wrapperRect.top + top
      }
    },

    updatePreviewLayoutBox() {
      this.previewLayoutBox = this.getPreviewLayoutBox()
    },

    handlePreviewResize() {
      this.$nextTick(() => {
        this.updatePreviewLayoutBox()
      })
    },

    observePreviewResize() {
      if (this.previewResizeObserver) {
        this.previewResizeObserver.disconnect()
        this.previewResizeObserver = null
      }

      if (typeof ResizeObserver === 'undefined') {
        return
      }

      const preview = this.getPreviewElement()
      const wrapper = this.$refs.previewWrapper

      if (!preview || !wrapper) {
        return
      }

      this.previewResizeObserver = new ResizeObserver(() => {
        this.updatePreviewLayoutBox()
      })
      this.previewResizeObserver.observe(preview)
      this.previewResizeObserver.observe(wrapper)
    },

    getPreviewLayerStyle() {
      const previewBox = this.previewLayoutBox

      return {
        ...this.getImageTransform(),
        left: `${previewBox.left}px`,
        top: `${previewBox.top}px`,
        width: `${previewBox.width}px`,
        height: `${previewBox.height}px`
      }
    },

    clampPercent(value) {
      return Math.min(100, Math.max(0, value))
    },

    async openMarkupChat(markup) {
      this.chatMarkup = markup
      this.showChatDialog = true
      this.loadingChat = true

      try {
        const response = await api.content.markup.getMarkupChatByMarkup(markup.markup_id)
        this.chatMessages = response.data.items
      } catch (error) {
        console.error('Error loading chat:', error)
      } finally {
        this.loadingChat = false
      }
    },

    closeChatDialog() {
      this.showChatDialog = false
      this.chatMarkup = null
      this.chatMessages = []
      this.newChatMessage = ''
    },

    async sendChatMessage() {
      if (!this.newChatMessage || !this.chatMarkup) return

      this.sendingChat = true
      try {
        const response = await api.content.markup.createMarkupChat({
          markup_id: this.chatMarkup.markup_id,
          message: this.newChatMessage
        })

        this.chatMessages.push(response.data)
        this.newChatMessage = ''

        // Update chat count on the markup
        this.chatMarkup.chat_count = (this.chatMarkup.chat_count || 0) + 1

        this.$store.dispatch('app/showSnackbar', {
          text: 'Mensagem enviada',
          color: 'success'
        })
      } catch (error) {
        console.error('Error sending chat message:', error)
        this.$store.dispatch('app/showSnackbar', {
          text: 'Erro ao enviar mensagem',
          color: 'error'
        })
      } finally {
        this.sendingChat = false
      }
    },

    formatChatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    handleDrawStart(event) {
      if (!this.addMarkupMode) return
      if (this.newMarkupShape === 'point') return // Point doesn't need drawing

      const { x, y } = this.getDocumentCoordsFromEvent(event)

      this.isDrawing = true
      this.drawStartPos = { x, y }
      this.drawCurrentPos = { x, y }
    },

    handleDrawMove(event) {
      if (!this.isDrawing) return

      const { x, y } = this.getDocumentCoordsFromEvent(event)

      this.drawCurrentPos = { x, y }
    },

    handleDrawEnd(event) {
      if (!this.isDrawing) return

      const { x, y } = this.getDocumentCoordsFromEvent(event)

      // Calculate center point and dimensions
      const centerX = (this.drawStartPos.x + x) / 2
      const centerY = (this.drawStartPos.y + y) / 2
      const width = Math.abs(x - this.drawStartPos.x)
      const height = Math.abs(y - this.drawStartPos.y)

      // Only create markup if area is large enough
      if (width > 2 && height > 2) {
        this.pendingMarkupCoords = {
          x: centerX,
          y: centerY,
          width,
          height
        }
        this.showAddMarkupDialog = true
      }

      // Reset drawing state
      this.isDrawing = false
      this.drawStartPos = null
      this.drawCurrentPos = null
    },

    getDrawingPreviewStyle() {
      if (!this.drawStartPos || !this.drawCurrentPos) return {}

      const left = Math.min(this.drawStartPos.x, this.drawCurrentPos.x)
      const top = Math.min(this.drawStartPos.y, this.drawCurrentPos.y)
      const width = Math.abs(this.drawCurrentPos.x - this.drawStartPos.x)
      const height = Math.abs(this.drawCurrentPos.y - this.drawStartPos.y)

      return {
        left: `${left}%`,
        top: `${top}%`,
        width: `${width}%`,
        height: `${height}%`,
        border: `3px solid ${this.newMarkupColor}`,
        position: 'absolute',
        pointerEvents: 'none'
      }
    },

    zoomIn() {
      if (this.zoomLevel < 200) {
        this.zoomLevel = Math.min(200, this.zoomLevel + 25)
      }
    },

    zoomOut() {
      if (this.zoomLevel > 50) {
        this.zoomLevel = Math.max(50, this.zoomLevel - 25)
      }
    },

    toggleFullscreen() {
      const elem = this.$el
      if (!document.fullscreenElement) {
        elem.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`)
        })
      } else {
        document.exitFullscreen()
      }
    },

    toggleMagnifier() {
      this.magnifierMode = !this.magnifierMode
      if (!this.magnifierMode) {
        this.magnifierPos = null
      }
    },

    toggleMarkups() {
      this.markupsVisible = !this.markupsVisible
    },

    handleMagnifierMove(event) {
      if (!this.magnifierMode) return

      const img = event.target
      const wrapper = this.$refs.previewWrapper

      if (!wrapper) return

      const imgRect = img.getBoundingClientRect()
      const wrapperRect = wrapper.getBoundingClientRect()

      // Get cursor position relative to the wrapper (not the image)
      const x = event.clientX - wrapperRect.left
      const y = event.clientY - wrapperRect.top

      this.magnifierPos = {
        x: x,
        y: y,
        imgWidth: imgRect.width,
        imgHeight: imgRect.height,
        imgOffsetX: imgRect.left - wrapperRect.left,
        imgOffsetY: imgRect.top - wrapperRect.top
      }
    },

    hideMagnifier() {
      if (this.magnifierMode) {
        this.magnifierPos = null
      }
    },

    getMagnifierStyle() {
      if (!this.magnifierPos) return {}

      const magnifierSize = 200
      const magnificationFactor = 2.5

      // Calculate the background size to show the magnified image
      const bgWidth = this.magnifierPos.imgWidth * magnificationFactor
      const bgHeight = this.magnifierPos.imgHeight * magnificationFactor

      // Calculate cursor position relative to the image
      const cursorOnImageX = this.magnifierPos.x - this.magnifierPos.imgOffsetX
      const cursorOnImageY = this.magnifierPos.y - this.magnifierPos.imgOffsetY

      // Calculate the background position to center on cursor position on the image
      const bgPosX = -((cursorOnImageX * magnificationFactor) - magnifierSize / 2)
      const bgPosY = -((cursorOnImageY * magnificationFactor) - magnifierSize / 2)

      return {
        position: 'absolute',
        left: `${this.magnifierPos.x - magnifierSize / 2}px`,
        top: `${this.magnifierPos.y - magnifierSize / 2}px`,
        width: `${magnifierSize}px`,
        height: `${magnifierSize}px`,
        border: '3px solid #2196F3',
        borderRadius: '50%',
        cursor: 'none',
        backgroundImage: `url(${this.currentPreviewUrl})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${bgWidth}px ${bgHeight}px`,
        backgroundPosition: `${bgPosX}px ${bgPosY}px`,
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        zIndex: 1000
      }
    },

    getImageTransform() {
      const scale = this.zoomLevel / 100
      const translate = `translate(${this.panOffset.x}px, ${this.panOffset.y}px)`
      return {
        transform: `${translate} scale(${scale})`,
        transformOrigin: 'center center'
      }
    },

    handlePanStart(event) {
      // Only pan when zoomed in and not in markup mode
      if (this.zoomLevel <= 100 || this.addMarkupMode || this.magnifierMode) return

      event.preventDefault()
      this.isPanning = true
      this.panStart = {
        x: event.clientX - this.panOffset.x,
        y: event.clientY - this.panOffset.y
      }
    },

    handlePanMove(event) {
      // Handle magnifier if active
      if (this.magnifierMode && !this.isPanning) {
        this.handleMagnifierMove(event)
        return
      }

      if (!this.isPanning) return

      event.preventDefault()
      this.panOffset = {
        x: event.clientX - this.panStart.x,
        y: event.clientY - this.panStart.y
      }

      // Hide magnifier when panning
      this.magnifierPos = null
    },

    handlePanEnd() {
      this.isPanning = false
    },

    handleVideoClick(event) {
      // Handle markup creation on video
      if (this.addMarkupMode) {
        event.preventDefault()
        event.stopPropagation()

        // Only handle point markups on click. Rectangle/Circle use drawing.
        if (this.newMarkupShape === 'point') {
          const { x, y } = this.getDocumentCoordsFromEvent(event)

          this.pendingMarkupCoords = { x, y }
          this.showAddMarkupDialog = true
        }
      }
    }
  }
}
</script>

<style scoped>
.markup-session {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  overflow: hidden;
}

/* Embedded mode - fill parent container instead of viewport */
.markup-session-embedded {
  height: 100%;
}

.markup-content-embedded {
  height: 100%;
  overflow: hidden;
}

.markup-session >>> .v-toolbar {
  flex-shrink: 0 !important;
  max-height: 64px !important;
  min-height: 64px !important;
  height: 64px !important;
}

.markup-session >>> .v-toolbar__content {
  height: 64px !important;
  padding: 0 16px !important;
}

.markup-content {
  flex: 1;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.markup-session-container {
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  padding: 0 !important;
  margin: 0 !important;
}

.markup-session-container.v-container {
  padding: 0 !important;
  margin: 0 !important;
}

.markup-session-container >>> .v-container {
  height: 100%;
  max-height: 100%;
  padding: 0 !important;
  margin: 0 !important;
}

.markup-session-container >>> .row {
  height: 100%;
  max-height: 100%;
  margin: 0 !important;
  padding: 0 !important;
}

.bottom-navigation {
  flex-shrink: 0;
  border-top: 2px solid rgba(255, 255, 255, 0.22);
  background-color:  #424242;
  width: 100%;
}

.preview-column {
  background-color: #424242;
  display: flex;
  position: relative;
  overflow: hidden;
  height: 100%;
  max-height: 100%;
  flex-direction: column;
}

.preview-container {
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  overflow: hidden;
}

.preview-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-image,
.preview-video {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.markup-transform-layer,
.markup-indicators {
  position: absolute;
  pointer-events: none;
}

.markup-indicator {
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
}

.markup-indicator:hover {
  transform: translate(-50%, -50%) scale(1.1);
  z-index: 20;
}

.markup-indicator-selected {
  z-index: 30;
  animation: pulse 1s infinite;
}

.markup-indicator-resolved {
  opacity: 0.5;
}

.markup-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.add-markup-cursor {
  cursor: crosshair;
}

.can-pan {
  cursor: grab;
}

.can-pan:active {
  cursor: grabbing;
}

.sidebar-column {
  background-color: #fafafa;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  overflow-y: auto;
  height: 100%;
  max-height: 100%;
}

.markup-session-container >>> .col {
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.page-navigation {
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  z-index: 100;
  gap: 16px;
}

.page-nav-btn {
  pointer-events: auto;
  background-color: white !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.page-nav-left {
  margin-left: 0;
}

.page-nav-right {
  margin-right: 0;
}

.page-indicator {
  pointer-events: auto;
  font-size: 16px;
  font-weight: 600;
}

.chat-messages {
  max-height: 400px;
  overflow-y: auto;
}

.text-wrap {
  white-space: normal !important;
  word-wrap: break-word;
}

.drawing-preview {
  background-color: rgba(33, 150, 243, 0.1);
  z-index: 50;
  pointer-events: none;
}

.drawing-preview-circle {
  border-radius: 50% !important;
}

.markup-indicator-shape {
  display: flex;
  align-items: center;
  justify-content: center;
}

.shape-markup-number {
  position: absolute;
  top: -14px;
  left: -14px;
  z-index: 1;
}

.custom-toolbar {
  width: 100%;
}

@keyframes pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.15);
  }
}
</style>
